import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  generateUmk,
  deriveWrapKey,
  wrapUmk,
  unwrapUmk,
  encryptCredentials,
  decryptCredentials,
  encodeWrappedBlob,
  decodeWrappedBlob,
  encodeCredentialsBlob,
  decodeCredentialsBlob,
  detectPrfSupport,
  umkWrapSalt,
  assertPrf,
  extractPrfFromRegistration,
  wipe,
  type ChequeCredentials,
  type EncryptedCredentials,
} from "@/lib/e2ee";

interface PasskeyUser {
  userId: string;
  displayName?: string;
}

export interface AuthResult {
  success: boolean;
  cancelled?: boolean;
  stage?: string;
  code?: string;
}

export interface PendingChequeCreds {
  chequeId: string;
  urlKey: string;
  pin: string;
}

interface PasskeyAuthContextType {
  user: PasskeyUser | null;
  loading: boolean;
  authenticate: () => Promise<AuthResult>;
  logout: () => void;
  linkCheque: (
    chequeId: string,
    role: "sender" | "recipient",
    creds?: ChequeCredentials,
  ) => Promise<boolean>;
  unlinkCheque: (chequeId: string) => Promise<boolean>;
  isSupported: boolean;
  umk: Uint8Array | null;
  hasUmk: boolean;
  unlockUmk: () => Promise<boolean>;
  lockUmk: () => void;
  registerAndWrapUmk: (pending: PendingChequeCreds[]) => Promise<AuthResult>;
  decryptChequeCredentials: (blob: EncryptedCredentials) => Promise<ChequeCredentials | null>;
  prfSupported: boolean | null;
}

const PasskeyAuthContext = createContext<PasskeyAuthContextType | null>(null);

const TOKEN_KEY = "postcash_passkey_token";
const HAS_CREDENTIAL_KEY = "postcash_has_passkey";
export const PENDING_LINKS_KEY = "postcash_pending_links";

/** Fire-and-forget: link all cheques saved while the user was logged out.
 *  Failed links are kept in localStorage for retry on next session. */
async function processPendingLinks() {
  try {
    const raw = localStorage.getItem(PENDING_LINKS_KEY);
    if (!raw) return;
    const links: Array<{ chequeId: string; role: string }> = JSON.parse(raw);
    if (!links.length) return;

    const failed: typeof links = [];
    await Promise.all(
      links.map((l) =>
        invokePasskey("link-cheque", { chequeId: l.chequeId, role: l.role })
          .catch(() => { failed.push(l); })
      )
    );

    if (failed.length > 0) {
      localStorage.setItem(PENDING_LINKS_KEY, JSON.stringify(failed));
    } else {
      localStorage.removeItem(PENDING_LINKS_KEY);
    }
  } catch { /* ignore */ }
}

// Safe base64url encode that handles large buffers (no spread operator)
function bufToB64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function b64urlToBuf(b64: string): ArrayBuffer {
  const str = b64.replace(/-/g, "+").replace(/_/g, "/");
  const pad = str.length % 4 === 0 ? "" : "=".repeat(4 - (str.length % 4));
  const binary = atob(str + pad);
  return Uint8Array.from(binary, (c) => c.charCodeAt(0)).buffer;
}

function generateFlowId(): string {
  return Math.random().toString(36).substring(2, 10);
}

async function invokePasskey(action: string, extra: Record<string, unknown> = {}) {
  const token = localStorage.getItem(TOKEN_KEY) || "";
  const flowId = extra.flowId || generateFlowId();
  console.log(`[passkey][${flowId}] invoking ${action}...`);
  const data = await api.invoke("passkey-auth", { action, token, flowId, ...extra });
  console.log(`[passkey][${flowId}] ${action} success`);
  return data;
}

function safeGetTransports(response: AuthenticatorAttestationResponse): string[] {
  try {
    return response.getTransports?.() || ["internal"];
  } catch {
    return ["internal"];
  }
}

async function checkUVPAA(): Promise<boolean> {
  try {
    if (typeof PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable === "function") {
      return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    }
  } catch { /* ignore */ }
  return false;
}

export function PasskeyAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PasskeyUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [prfSupported, setPrfSupported] = useState<boolean | null>(null);
  const umkRef = useRef<Uint8Array | null>(null);
  const [umkVersion, setUmkVersion] = useState(0); // tick to re-render on UMK change
  const umk = umkRef.current;
  const hasUmk = umk !== null;

  const isSupported =
    typeof window !== "undefined" &&
    !!window.PublicKeyCredential &&
    typeof window.PublicKeyCredential === "function";

  const setUmk = useCallback((next: Uint8Array | null) => {
    if (umkRef.current && umkRef.current !== next) {
      wipe(umkRef.current);
    }
    umkRef.current = next;
    setUmkVersion((v) => v + 1);
  }, []);

  const lockUmk = useCallback(() => {
    setUmk(null);
  }, [setUmk]);

  // Detect PRF support on mount
  useEffect(() => {
    if (!isSupported) {
      setPrfSupported(false);
      return;
    }
    detectPrfSupport().then(setPrfSupported).catch(() => setPrfSupported(false));
  }, [isSupported]);

  // Verify existing session on mount
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    invokePasskey("verify-session")
      .then((data) => {
        if (data?.valid && data?.userId) {
          setUser({ userId: data.userId });
          processPendingLinks();
        } else {
          localStorage.removeItem(TOKEN_KEY);
        }
      })
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setLoading(false));
  }, []);

  // Wipe UMK on tab hide / pagehide for defense in depth
  useEffect(() => {
    const onHide = () => { lockUmk(); };
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, [lockUmk]);

  const tryLogin = useCallback(async (flowId: string): Promise<AuthResult> => {
    try {
      console.log(`[passkey][${flowId}] starting login flow...`);
      const options = await invokePasskey("login-challenge", { flowId });

      const publicKeyOptions: PublicKeyCredentialRequestOptions = {
        challenge: b64urlToBuf(options.challenge),
        timeout: options.timeout,
        userVerification: (options.userVerification || "preferred") as UserVerificationRequirement,
        allowCredentials: [],
      };

      if (options.rpId) {
        publicKeyOptions.rpId = options.rpId;
      }

      const credential = (await navigator.credentials.get({
        publicKey: publicKeyOptions,
      })) as PublicKeyCredential | null;

      if (!credential) {
        console.log(`[passkey][${flowId}] login: null credential`);
        return { success: false, cancelled: true, stage: "login-get", code: "USER_CANCELLED" };
      }

      console.log(`[passkey][${flowId}] login: got credential, verifying...`);
      const response = credential.response as AuthenticatorAssertionResponse;

      const result = await invokePasskey("login-verify", {
        flowId,
        challengeId: options.challengeId,
        credential: {
          id: credential.id,
          rawId: bufToB64url(credential.rawId),
          response: {
            authenticatorData: bufToB64url(response.authenticatorData),
            clientDataJSON: bufToB64url(response.clientDataJSON),
            signature: bufToB64url(response.signature),
            userHandle: response.userHandle ? bufToB64url(response.userHandle) : undefined,
          },
          type: credential.type,
        },
      });

      if (result?.token) {
        localStorage.setItem(TOKEN_KEY, result.token);
        localStorage.setItem(HAS_CREDENTIAL_KEY, "1");
        setUser({ userId: result.userId, displayName: result.displayName });
        processPendingLinks();
        return { success: true, stage: "login-verify" };
      }

      return { success: false, stage: "login-verify", code: "NO_TOKEN" };
    } catch (e: any) {
      const name = e?.name || "";
      const code = e?.code || "";

      if (name === "NotAllowedError" || name === "AbortError") {
        console.warn(`[passkey][${flowId}] login prompt rejected: ${name}`);
        return { success: false, cancelled: true, stage: "login-get", code: "USER_CANCELLED" };
      }

      if (code === "UNKNOWN_CREDENTIAL") {
        console.log(`[passkey][${flowId}] server: unknown credential, will try register`);
        return { success: false, stage: "login-verify", code: "UNKNOWN_CREDENTIAL" };
      }

      console.error(`[passkey][${flowId}] login failed:`, e);
      return { success: false, stage: "login", code: code || name || "UNKNOWN" };
    }
  }, []);

  /** Register a new credential. If `withPrf` is true, requests the PRF extension
   *  on create() so we can derive the UMK wrap key in a single prompt when supported. */
  const tryRegister = useCallback(async (
    flowId: string,
    withPrf = false,
  ): Promise<AuthResult & { prfFirst?: Uint8Array; credentialRawId?: ArrayBuffer }> => {
    try {
      const uvpaa = await checkUVPAA();
      console.log(`[passkey][${flowId}] UVPAA=${uvpaa} (proceeding regardless), withPrf=${withPrf}`);

      console.log(`[passkey][${flowId}] starting register flow...`);
      const options = await invokePasskey("register-challenge", { flowId });

      console.log(`[passkey][${flowId}] register: creating credential (rpId=${options.rp?.id})...`);

      const extensions: any = { credProps: true };
      if (withPrf) {
        extensions.prf = { eval: { first: umkWrapSalt() } };
      }

      const createOptions: PublicKeyCredentialCreationOptions = {
        challenge: b64urlToBuf(options.challenge),
        rp: { name: options.rp.name },
        user: {
          id: new TextEncoder().encode(options.user.id),
          name: options.user.name,
          displayName: options.user.displayName,
        },
        pubKeyCredParams: options.pubKeyCredParams,
        timeout: options.timeout,
        authenticatorSelection: {
          residentKey: "preferred",
          userVerification: "preferred",
        },
        attestation: options.attestation,
        extensions,
      };

      if (options.rp?.id) {
        createOptions.rp.id = options.rp.id;
      }

      const credential = (await navigator.credentials.create({
        publicKey: createOptions,
      })) as PublicKeyCredential | null;

      if (!credential) {
        console.log(`[passkey][${flowId}] register: null credential`);
        return { success: false, cancelled: true, stage: "register-create", code: "USER_CANCELLED" };
      }

      const extResults = (credential as any).getClientExtensionResults?.();
      if (extResults?.credProps) {
        console.log(`[passkey][${flowId}] register: credProps.rk=${extResults.credProps.rk}`);
      }

      const prfFirst = withPrf ? extractPrfFromRegistration(credential) : null;
      console.log(`[passkey][${flowId}] register: prf-on-create=${prfFirst ? "yes" : "no"}`);

      console.log(`[passkey][${flowId}] register: credential created, verifying...`);
      const response = credential.response as AuthenticatorAttestationResponse;

      const result = await invokePasskey("register-verify", {
        flowId,
        challengeId: options.challengeId,
        credential: {
          id: credential.id,
          rawId: bufToB64url(credential.rawId),
          response: {
            attestationObject: bufToB64url(response.attestationObject),
            clientDataJSON: bufToB64url(response.clientDataJSON),
            transports: safeGetTransports(response),
          },
          type: credential.type,
        },
      });

      if (result?.token) {
        localStorage.setItem(TOKEN_KEY, result.token);
        localStorage.setItem(HAS_CREDENTIAL_KEY, "1");
        setUser({ userId: result.userId, displayName: result.displayName });
        processPendingLinks();
        console.log(`[passkey][${flowId}] register: success!`);
        return {
          success: true,
          stage: "register-verify",
          prfFirst: prfFirst || undefined,
          credentialRawId: credential.rawId,
        };
      }

      console.error(`[passkey][${flowId}] register: no token in result`, result);
      return { success: false, stage: "register-verify", code: "NO_TOKEN" };
    } catch (e: any) {
      const name = e?.name || "";
      const code = e?.code || "";

      if (name === "InvalidStateError") {
        console.warn(`[passkey][${flowId}] register invalid state (likely existing credential)`);
        return { success: false, stage: "register-create", code: "EXISTING_CREDENTIAL" };
      }

      if (name === "NotAllowedError" || name === "AbortError") {
        console.warn(`[passkey][${flowId}] register prompt rejected: ${name}`);
        return { success: false, cancelled: true, stage: "register-create", code: "USER_CANCELLED" };
      }

      console.error(`[passkey][${flowId}] registration failed:`, e);
      return { success: false, stage: "register", code: code || name || "UNKNOWN" };
    }
  }, []);

  const authenticate = useCallback(async (): Promise<AuthResult> => {
    const flowId = generateFlowId();
    const hasRegistered = localStorage.getItem(HAS_CREDENTIAL_KEY) === "1";

    if (hasRegistered) {
      const loginResult = await tryLogin(flowId);
      if (loginResult.success) return loginResult;
      if (loginResult.cancelled) return loginResult;

      if (loginResult.code === "UNKNOWN_CREDENTIAL") {
        console.log(`[passkey][${flowId}] login returned UNKNOWN_CREDENTIAL, trying register...`);
        return await tryRegister(flowId);
      }

      return loginResult;
    }

    console.log(`[passkey][${flowId}] no local hint, trying register first`);
    const registerResult = await tryRegister(flowId);
    if (registerResult.success || registerResult.cancelled) {
      return registerResult;
    }

    if (registerResult.code === "UVPAA_UNSUPPORTED") {
      return registerResult;
    }

    if (registerResult.code === "EXISTING_CREDENTIAL") {
      console.log(`[passkey][${flowId}] register failed (${registerResult.code}), trying login fallback...`);
      const loginResult = await tryLogin(flowId);
      if (loginResult.success) return loginResult;
      if (!loginResult.cancelled) return loginResult;
      return registerResult;
    }

    return registerResult;
  }, [tryLogin, tryRegister]);

  /** Register a new passkey with PRF, derive UMK, store wrapped UMK on the
   *  new credential, and batch-encrypt the supplied pending cheques in a
   *  single flow. Caller passes plaintext credentials in memory only; nothing
   *  is persisted before encryption succeeds. */
  const registerAndWrapUmk = useCallback(async (
    pending: PendingChequeCreds[],
  ): Promise<AuthResult> => {
    const flowId = generateFlowId();

    if (prfSupported === false) {
      return { success: false, stage: "register-prf", code: "PRF_UNSUPPORTED" };
    }

    const reg = await tryRegister(flowId, true);
    if (!reg.success) return reg;

    let prfOutput: Uint8Array | null = reg.prfFirst || null;

    // Fallback for browsers that don't honor PRF on create (e.g. Safari today):
    // do an immediate get() on the just-created credential with the same salt.
    if (!prfOutput && reg.credentialRawId) {
      try {
        const challenge = crypto.getRandomValues(new Uint8Array(32));
        prfOutput = await assertPrf(
          [new Uint8Array(reg.credentialRawId)],
          umkWrapSalt(),
          challenge,
        );
      } catch (e) {
        console.warn(`[passkey][${flowId}] follow-up assertPrf failed:`, e);
      }
    }

    if (!prfOutput) {
      console.warn(`[passkey][${flowId}] PRF unavailable on this authenticator, rolling back`);
      try {
        await invokePasskey("rollback-registration", {
          credentialId: bufToB64url(reg.credentialRawId!),
        });
      } catch { /* best effort */ }
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(HAS_CREDENTIAL_KEY);
      setUser(null);
      return { success: false, stage: "register-prf", code: "PRF_UNAVAILABLE" };
    }

    try {
      const wrapKey = await deriveWrapKey(prfOutput);
      const newUmk = generateUmk();
      const wrapped = await wrapUmk(newUmk, wrapKey);
      const wire = encodeWrappedBlob(wrapped);

      await invokePasskey("umk-register", {
        credentialId: bufToB64url(reg.credentialRawId!),
        wrappedUmk: wire.wrappedUmk,
        umkNonce: wire.umkNonce,
        version: wire.version,
      });

      // Encrypt + link each pending cheque in parallel.
      await Promise.all(
        pending.map(async (p) => {
          try {
            const blob = await encryptCredentials(newUmk, { urlKey: p.urlKey, pin: p.pin });
            const wireCreds = encodeCredentialsBlob(blob);
            await invokePasskey("link-cheque", {
              chequeId: p.chequeId,
              role: "sender",
              ciphertext: wireCreds.ciphertext,
              nonce: wireCreds.nonce,
              version: wireCreds.version,
            });
          } catch (err) {
            console.error(`[passkey][${flowId}] failed to link/encrypt ${p.chequeId}:`, err);
          }
        }),
      );

      setUmk(newUmk);
      // PRF output and wrap key are about to fall out of scope; wipe PRF for hygiene.
      wipe(prfOutput);
      return { success: true, stage: "register-umk" };
    } catch (err: any) {
      console.error(`[passkey][${flowId}] UMK provisioning failed:`, err);
      return { success: false, stage: "umk-register", code: "UMK_REGISTER_FAILED" };
    }
  }, [prfSupported, tryRegister, setUmk]);

  /** Unlock the UMK by performing a passkey assertion with PRF on one of the
   *  user's registered credentials, fetching its wrapped UMK, and unwrapping.
   *  Returns true on success; UMK then lives in ref until lockUmk(). */
  const unlockUmk = useCallback(async (): Promise<boolean> => {
    if (umkRef.current) return true;
    const flowId = generateFlowId();

    if (prfSupported === false) {
      console.warn(`[passkey][${flowId}] unlockUmk: PRF unsupported`);
      return false;
    }

    try {
      const list = await invokePasskey("umk-list-credentials");
      const wrapped: Array<{ credentialId: string; hasWrap: boolean }> = list?.credentials || [];
      const wrappedIds = wrapped.filter((c) => c.hasWrap).map((c) => c.credentialId);
      if (wrappedIds.length === 0) {
        console.warn(`[passkey][${flowId}] unlockUmk: no credential has a wrapped UMK`);
        return false;
      }

      const credentialIds = wrappedIds.map((id) => new Uint8Array(b64urlToBuf(id)));
      const challenge = crypto.getRandomValues(new Uint8Array(32));
      const prfOutput = await assertPrf(credentialIds, umkWrapSalt(), challenge);
      if (!prfOutput) {
        console.warn(`[passkey][${flowId}] unlockUmk: authenticator omitted PRF`);
        return false;
      }

      // Identify which credential the user actually used. The PRF assertion above
      // returned via navigator.credentials.get(); to know which credential id was
      // used we need to look at the assertion. For simplicity we re-do the lookup
      // by trying each candidate's wrap until one decrypts successfully — bounded
      // by `wrappedIds.length` which is small (≤ a handful of credentials).
      const wrapKey = await deriveWrapKey(prfOutput);
      let recovered: Uint8Array | null = null;
      for (const credId of wrappedIds) {
        try {
          const wrapResp = await invokePasskey("umk-get-wrap", { credentialId: credId });
          if (!wrapResp?.hasWrap) continue;
          const blob = decodeWrappedBlob({
            wrappedUmk: wrapResp.wrappedUmk,
            umkNonce: wrapResp.umkNonce,
            version: wrapResp.version,
          });
          recovered = await unwrapUmk(blob, wrapKey);
          break;
        } catch { /* try next */ }
      }

      wipe(prfOutput);

      if (!recovered) {
        console.warn(`[passkey][${flowId}] unlockUmk: no wrap could be unwrapped`);
        return false;
      }

      setUmk(recovered);
      return true;
    } catch (err) {
      console.error(`[passkey][${flowId}] unlockUmk failed:`, err);
      return false;
    }
  }, [prfSupported, setUmk]);

  const decryptChequeCredentials = useCallback(async (
    blob: EncryptedCredentials,
  ): Promise<ChequeCredentials | null> => {
    const key = umkRef.current;
    if (!key) return null;
    try {
      return await decryptCredentials(key, blob);
    } catch (err) {
      console.error("decryptChequeCredentials failed:", err);
      return null;
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setUmk(null);
  }, [setUmk]);

  const linkCheque = useCallback(async (
    chequeId: string,
    role: "sender" | "recipient",
    creds?: ChequeCredentials,
  ): Promise<boolean> => {
    try {
      let extra: Record<string, unknown> = { chequeId, role };
      if (creds && role === "sender" && umkRef.current) {
        const blob = await encryptCredentials(umkRef.current, creds);
        const wire = encodeCredentialsBlob(blob);
        extra = { ...extra, ciphertext: wire.ciphertext, nonce: wire.nonce, version: wire.version };
      }
      const result = await invokePasskey("link-cheque", extra);
      return result?.success === true;
    } catch (err) {
      console.error("Link cheque failed:", err);
      return false;
    }
  }, []);

  const unlinkCheque = useCallback(async (chequeId: string): Promise<boolean> => {
    try {
      const result = await invokePasskey("unlink-cheque", { chequeId });
      return result?.success === true;
    } catch (err) {
      console.error("Unlink cheque failed:", err);
      return false;
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      authenticate,
      logout,
      linkCheque,
      unlinkCheque,
      isSupported,
      umk,
      hasUmk,
      unlockUmk,
      lockUmk,
      registerAndWrapUmk,
      decryptChequeCredentials,
      prfSupported,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading, authenticate, logout, linkCheque, unlinkCheque, isSupported, umkVersion, unlockUmk, lockUmk, registerAndWrapUmk, decryptChequeCredentials, prfSupported],
  );

  return (
    <PasskeyAuthContext.Provider value={value}>
      {children}
    </PasskeyAuthContext.Provider>
  );
}

export function usePasskeyAuth() {
  const ctx = useContext(PasskeyAuthContext);
  if (!ctx) throw new Error("usePasskeyAuth must be used within PasskeyAuthProvider");
  return ctx;
}

/**
 * Shared error handler for passkey auth results.
 * Maps error codes to user-friendly translated toast messages.
 */
export function showAuthError(result: AuthResult, t: (key: string, params?: Record<string, any>) => string) {
  const stage = result.stage || "unknown";
  const code = result.code || "UNKNOWN";
  console.warn(`[passkey] auth failed: stage=${stage}, code=${code}`);

  const messages: Record<string, string> = {
    CHALLENGE_INVALID: t("auth.session_expired"),
    CHALLENGE_MISMATCH: t("auth.verification_error"),
    INVALID_RP_ID: t("auth.domain_mismatch"),
    ORIGIN_MISMATCH: t("auth.domain_mismatch"),
    UV_REQUIRED: t("auth.biometry_required"),
    INVALID_SIGNATURE: t("auth.signature_error"),
    CLONE_DETECTED: t("auth.key_cloned"),
    NO_TOKEN: t("auth.no_token"),
    EXISTING_CREDENTIAL: t("auth.passkey_exists"),
    UNKNOWN_CREDENTIAL: t("auth.passkey_not_found"),
    UVPAA_UNSUPPORTED: t("auth.device_not_supported"),
    PRF_UNSUPPORTED: t("auth.prf_unsupported"),
    PRF_UNAVAILABLE: t("auth.prf_unsupported"),
    UMK_REGISTER_FAILED: t("auth.encryption_setup_failed"),
  };

  const msg = messages[code] || t("auth.auth_error_code", { code });
  toast.error(msg);
}
